import type { SettingsView } from "@buildpy/core/views/g2c/settings";
import SettingsBrand from "@/modules/org/ui/settings-brand";
import SettingsCodes from "@/modules/org/ui/settings-codes";
import SettingsCompany from "@/modules/org/ui/settings-company";
import SettingsExport from "@/modules/org/ui/settings-export";
import SettingsContract from "@/modules/org/ui/settings-contract";
import SettingsCostAccess from "@/modules/org/ui/settings-cost-access";
import SettingsMembers from "@/modules/org/ui/settings-members";
import SettingsIntake from "@/modules/org/ui/settings-intake";
import SettingsIntakeMode from "@/modules/org/ui/settings-intake-mode";
import SettingsLeave from "@/modules/org/ui/settings-leave";
import SettingsIntakeLink from "@/modules/org/ui/settings-intake-link";
import SettingsPlan from "@/modules/org/ui/settings-plan";
import SettingsPortfolio from "@/modules/org/ui/settings-portfolio";
import SettingsProcessCalibration from "@/modules/org/ui/settings-process-calibration";
import SettingsProject from "@/modules/org/ui/settings-project";
import SettingsNotify from "@/modules/org/ui/settings-notify";
import SettingsPrices from "@/modules/org/ui/settings-prices";
import SettingsTrades from "@/modules/org/ui/settings-trades";
import SettingsTabs from "@/modules/org/ui/settings-tabs";
import SettingsTemplate from "@/modules/org/ui/settings-template";
import SettingsUsage from "@/modules/org/ui/settings-usage";
import PageHeader from "@/shell/page-header";

// 설정 — 탭은 `?tab=` 쿼리이고 뷰가 그 탭에 필요한 것만 읽는다 · 저장은 대표만(실장은 칸을 잠근다).
const Page = ({ data }: { data: SettingsView }) => {
  const { canEdit } = data;
  // 단가표 탭은 직원 수정 스위치(canEditPrices)를 따른다 — 켜진 직원에게 「읽기 전용」 을 띄우지 않는다.
  const staffPrices = data.tab === "prices" && !canEdit && data.canEditPrices;

  return (
    <>
      <PageHeader
        description={
          canEdit
            ? "회사 정보·견적 기본값·단가표·계약 기본값을 여기서 고칩니다."
            : staffPrices
              ? "단가표를 여기서 고칩니다. 마진 설정은 대표만 바꿀 수 있습니다."
              : "읽기 전용입니다. 바꾸려면 대표에게 요청하세요."
        }
        title="설정"
      />

      <SettingsTabs current={data.tab} />

      {data.tab === "company" && (
        <>
          <SettingsCompany
            canEdit={canEdit}
            value={data.company}
          />
          <SettingsPortfolio
            canEdit={canEdit}
            embedCode={data.portfolio.embedCode}
            enabled={data.portfolio.enabled}
            hiddenReviews={data.portfolio.hiddenReviews}
            inquiryEnabled={data.portfolio.inquiryEnabled}
          />
          <SettingsBrand
            canEdit={canEdit}
            enabled={data.brandFooter}
          />
          <SettingsCostAccess
            canEdit={canEdit}
            staffEdit={data.costAccess.staffEdit}
            staffView={data.costAccess.staffView}
          />
          <SettingsProcessCalibration
            canEdit={canEdit}
            crewSize={data.processCalibration.crewSize}
            factor={data.processCalibration.factor}
          />
          {canEdit && <SettingsExport />}
          <SettingsPlan panel={data.plan} />
          <SettingsUsage usage={data.usage} />
          {canEdit && <SettingsLeave orgName={data.orgName} />}
        </>
      )}

      {data.tab === "template" && (
        <SettingsTemplate canEdit={canEdit} templates={data.templates} trades={data.trades} />
      )}

      {data.tab === "trades" && (
        <SettingsTrades canEdit={canEdit} phases={data.phases} trades={data.trades} />
      )}

      {data.tab === "prices" && (
        <SettingsPrices
          canEdit={data.canEditPrices}
          canEditMargins={canEdit}
          catalogCounts={data.catalogCounts}
          filter={data.filter}
          margins={data.margins}
          rows={data.rows}
          trades={data.trades}
        />
      )}

      {data.tab === "codes" && (
        <SettingsCodes canEdit={canEdit} groups={data.groups} />
      )}

      {data.tab === "contract" && (
        <>
          <SettingsContract canEdit={canEdit} {...data.contract} />
          <SettingsProject
            canEdit={canEdit}
            warrantyEnabled={data.warrantyEnabled}
          />
        </>
      )}

      {data.tab === "members" && <SettingsMembers canEdit={canEdit} invites={data.invites} members={data.members} />}

      {data.tab === "intake" && (
        <>
          <SettingsIntakeLink link={data.link} />
          <SettingsIntakeMode
            canEdit={canEdit}
            mode={data.mode}
          />
          <SettingsIntake
            canEdit={canEdit}
            enabled={data.designerChoice}
            members={data.members}
          />
        </>
      )}

      {data.tab === "notify" && (
        <SettingsNotify
          canEdit={canEdit}
          channels={data.channels}
          customer={data.customer}
          dryRun={data.dryRun}
          logs={data.logs}
          unansweredHours={data.unansweredHours}
        />
      )}
    </>
  );
};

export default Page;
